/** Phone queries: numbers and per-number config. */

import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { queryKeys } from '../query-keys';
import { useApiClient } from '../react/ApiClientProvider';
import {
  type PhoneNumber,
  type PhoneNumberConfig,
  phoneNumberConfigSchema,
  phoneNumberSchema,
} from '../responses';
import { unwrap } from './_helpers';

/** `GET /phone/numbers` — provisioned phone numbers for the hotel. */
export function usePhoneNumbers(tenantId: string, hotelId: string): UseQueryResult<PhoneNumber[]> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.phoneNumbers(tenantId, hotelId),
    queryFn: async () => {
      const result = await client.GET('/api/v1/tenants/{tenantId}/hotels/{hotelId}/phone/numbers', {
        params: { path: { tenantId, hotelId } },
        parseAs: 'json',
      });
      return unwrap(result, z.array(phoneNumberSchema));
    },
  });
}

/** `GET /phone/numbers/:phoneNumberId/config` — number + forwarding + working hours. */
export function usePhoneNumberConfig(
  tenantId: string,
  hotelId: string,
  phoneNumberId: string,
): UseQueryResult<PhoneNumberConfig> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.phoneNumberConfig(tenantId, hotelId, phoneNumberId),
    queryFn: async () => {
      const result = await client.GET(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/phone/numbers/{phoneNumberId}/config',
        { params: { path: { tenantId, hotelId, phoneNumberId } }, parseAs: 'json' },
      );
      return unwrap(result, phoneNumberConfigSchema);
    },
    enabled: phoneNumberId.length > 0,
  });
}
